import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import OutsideClickHandler from "react-outside-click-handler/build/OutsideClickHandler";
import moment from "moment";
import { useLocation, useNavigate } from "react-router-dom";

import { handleError } from "src/Common/function";
import { getAvatarUrl, getImageUrl } from "src/Common/common";
import FormTemplateService from "src/Services/API/formtemplate.service";
import Button from "src/Components/Button";
import CreateButton from "src/Components/CreateButton";
import ConfirmDialog from "src/Common/confirmdialog";
import CreateTemplateModal from "./CreateTemplateModal";
import FormTemplateFilterView from "./components/FormTemplateFilterView";
import { FORM_TYPES } from "./components/FormTypeDropdown";
import FormCreateModal from "src/Pages/WO/Components/forms/FormCreateModal";
import UserService from "src/Services/API/user.service";
import AddTemplateModal from "./components/AddTemplateModal";

const getFormTypeLabel = (value) => FORM_TYPES.find((t) => t.value === value)?.label || "";
const FREQUENCIES = [
  { value: 0, label: "One Time" },
  { value: 1, label: "Weekly" },
  { value: 2, label: "Monthly" },
  { value: 3, label: "Quarterly" },
  { value: 4, label: "Semi-Annual" },
  { value: 5, label: "Annual" },
];
const getFrequencyLabel = (value) =>
  FREQUENCIES.find((f) => f.value === Number(value))?.label || "-";

const TemplateList = ({ hideTabs = false, hideControls = false, customerId = null }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isCustomerContext = customerId !== null && customerId !== undefined;

  const [tab, setTab] = useState("Active");
  const [templates, setTemplates] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [appliedFilterType, setAppliedFilterType] = useState("");
  const [appliedFilterCustomerId, setAppliedFilterCustomerId] = useState("");

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [addTemplateModalOpen, setAddTemplateModalOpen] = useState(false);
  const [assignableTemplates, setAssignableTemplates] = useState([]);
  const [selectedTemplateForEdit, setSelectedTemplateForEdit] = useState(null);
  const [formCreateModalOpen, setFormCreateModalOpen] = useState(false);
  const [selectedTemplateForForm, setSelectedTemplateForForm] = useState(null);
  const [actionMenuId, setActionMenuId] = useState(null);
  const [confirmTemplateId, setConfirmTemplateId] = useState(null);

  const fetchTemplates = (
    isActive,
    formType = appliedFilterType,
    filterCustomerId = appliedFilterCustomerId
  ) => {
    const activeParam = isActive !== undefined ? isActive : (tab === "Active");
    FormTemplateService.getAll(
      String(activeParam),
      formType !== "" ? formType : "",
      isCustomerContext
        ? customerId
        : (filterCustomerId !== "" ? filterCustomerId : "")
    )
      .then((response) => {
        if (response.status === 200) {
          setTemplates(response.data);
        }
      })
      .catch((error) => handleError(error));
  };

  useEffect(() => {
    fetchTemplates(
      hideTabs ? true : tab === "Active",
      appliedFilterType,
      appliedFilterCustomerId
    );
  }, [tab, hideTabs, customerId, appliedFilterType, appliedFilterCustomerId]);

  const handleTabClick = (nextTab) => {
    setTab(nextTab);
    setActionMenuId(null);
  };

  const handleApplyFilter = ({ filterType, customerId: nextCustomerId }) => {
    setAppliedFilterType(filterType);
    setAppliedFilterCustomerId(nextCustomerId || "");
  };

  const handleClearFilter = () => {
    setAppliedFilterType("");
    setAppliedFilterCustomerId("");
  };

  const handleCreate = (data) => {
    const isEditing = selectedTemplateForEdit !== null;
    setCreateModalOpen(false);
    setAddTemplateModalOpen(false);
    navigate("/form-builder", {
      state: {
        mode: "template",
        templateDraft: data,
        templateName: data.name,
        ...(isEditing
          ? {
            templateId: selectedTemplateForEdit.id,
            formSchema: selectedTemplateForEdit.schema ?? null,
          }
          : {}),
        customerId: isCustomerContext ? customerId : undefined,
        from: isCustomerContext ? location.pathname : "/form-templates",
      },
    });
    setSelectedTemplateForEdit(null);
  };

  const handleAssignTemplate = (templateId) => {
    if (!isCustomerContext || !templateId) return;
    FormTemplateService.update(templateId, { customerId })
      .then(() => {
        toast("Template assigned");
        setAddTemplateModalOpen(false);
        fetchTemplates(hideTabs ? true : tab === "Active", appliedFilterType);
      })
      .catch((error) => handleError(error));
  };

  const openAddTemplateModal = () => {
    if (!isCustomerContext) {
      setCreateModalOpen(true);
      return;
    }

    FormTemplateService.getAll("true", "", "")
      .then((response) => {
        if (response.status === 200) {
          setAssignableTemplates(
            response.data.filter((t) => !t.customer)
          );
        }
        setAddTemplateModalOpen(true);
      })
      .catch((error) => handleError(error));
  };

  const handleArchive = (id) => {
    FormTemplateService.archive(id)
      .then(() => {
        toast("Template archived");
        setActionMenuId(null);
        fetchTemplates(tab === "Active", appliedFilterType);
      })
      .catch((error) => handleError(error));
  };

  const handleRestore = (id) => {
    FormTemplateService.restore(id)
      .then(() => {
        toast("Template restored");
        setActionMenuId(null);
        fetchTemplates(tab === "Active", appliedFilterType);
      })
      .catch((error) => handleError(error));
  };

  const handleEdit = (template) => {
    setSelectedTemplateForEdit(template);
    setCreateModalOpen(true);
    setActionMenuId(null);
  };

  const handleDuplicate = (template) => {
    const currentUser = UserService.getCurrentUser();
    const duplicatePayload = {
      name: `${template.name} - copy`,
      description: template.description,
      formType: template.formType,
      frequency: template.frequency ?? null,
      schema: template.schema ?? null,
      createdBy: currentUser?.id,
      customerId: isCustomerContext ? customerId : undefined,
    };

    FormTemplateService.create(duplicatePayload)
      .then(() => {
        toast("Template duplicated");
        setActionMenuId(null);
        fetchTemplates(tab === "Active", appliedFilterType);
      })
      .catch((error) => handleError(error));
  };

  const handlePlusAction = (template) => {
    setSelectedTemplateForForm(template);
    setFormCreateModalOpen(true);
    setActionMenuId(null);
  };

  const requestArchiveRestore = (id) => {
    setConfirmTemplateId(id);
    setActionMenuId(null);
  };

  const handleConfirmArchiveRestore = () => {
    if (confirmTemplateId === null) return;
    if (tab === "Active") {
      handleArchive(confirmTemplateId);
    } else {
      handleRestore(confirmTemplateId);
    }
    setConfirmTemplateId(null);
  };

  return (
    <div className="w-full bg-backgroundColor">
      <ConfirmDialog
        title={tab === "Active" ? "Archive Template" : "Restore Template"}
        description={
          tab === "Active"
            ? "Are you sure you want to archive this template?"
            : "Are you sure you want to restore this template?"
        }
        shown={confirmTemplateId !== null}
        onConfirmClicked={handleConfirmArchiveRestore}
        onCancelClicked={() => setConfirmTemplateId(null)}
      />

      <CreateTemplateModal
        shown={createModalOpen}
        mode={selectedTemplateForEdit ? "edit" : "create"}
        initialData={selectedTemplateForEdit}
        onCancelClicked={() => {
          setCreateModalOpen(false);
          setSelectedTemplateForEdit(null);
        }}
        onSubmitClicked={handleCreate}
      />
      <AddTemplateModal
        shown={addTemplateModalOpen}
        templates={assignableTemplates}
        onClose={() => setAddTemplateModalOpen(false)}
        onAssignTemplate={handleAssignTemplate}
        onCreateTemplate={() => {
          setAddTemplateModalOpen(false);
          setCreateModalOpen(true);
        }}
      />
      <FormCreateModal
        shown={formCreateModalOpen}
        onClose={() => {
          setFormCreateModalOpen(false);
          setSelectedTemplateForForm(null);
        }}
        initialTemplateId={selectedTemplateForForm?.id ?? null}
        initialTemplateSchema={selectedTemplateForForm?.schema ?? null}
        initialFormType={selectedTemplateForForm?.formType ?? null}
        initialFrequency={
          selectedTemplateForForm?.frequency !== null && selectedTemplateForForm?.frequency !== undefined
            ? String(selectedTemplateForForm.frequency)
            : ''
        }
      />

      {!hideControls && (
        <div className="w-full flex items-center justify-between py-[20px] px-[18px]">
          <div className="flex items-center">
            {!hideTabs && (
              <>
                <span
                  onClick={() => handleTabClick("Active")}
                  className={`cursor-pointer font-OpenSansRegular text-[14px] font-[600] px-4 ${tab === "Active" ? "text-blueColor" : "text-subTitleColor"
                    }`}
                >
                  Active
                </span>
                <div className="h-4 w-[1px] bg-borderColor"></div>
                <span
                  onClick={() => handleTabClick("Archived")}
                  className={`cursor-pointer font-OpenSansRegular text-[14px] font-[600] px-4 ${tab === "Archived" ? "text-blueColor" : "text-subTitleColor"
                    }`}
                >
                  Archived
                </span>
              </>
            )}
          </div>
          <div className="flex items-center gap-8">
            <Button onClicked={() => setFilterOpen(!filterOpen)} title="Filter" isHighlighted={filterOpen} />
            <CreateButton onClicked={() => setCreateModalOpen(true)} title="CREATE" />
          </div>
        </div>
      )}

      <div className={hideControls ? "" : "px-7"}>
        {!hideControls && filterOpen && (
          <FormTemplateFilterView
            onApply={handleApplyFilter}
            onClear={handleClearFilter}
          />
        )}

        <table className="w-full">
          <thead>
            <tr className="border-t-[1px] border-b-[1px] border-borderColor">
              <th className="border-r-[1px] border-borderColor">
                <div className="font-OpenSansRegular font-[700] text-[13px] flex items-center pl-4 w-[200px] h-[40px] text-titleColor">
                  Template Name
                </div>
              </th>
              <th className="w-min-[200px] w-full border-r-[1px] border-borderColor">
                <div className="font-OpenSansRegular font-[700] text-[13px] flex items-center pl-4 h-[40px] text-titleColor">
                  Description
                </div>
              </th>
              <th className="border-r-[1px] border-borderColor">
                <div className="font-OpenSansRegular font-[700] text-[13px] flex items-center pl-4 w-[200px] h-[40px] text-titleColor">
                  Type
                </div>
              </th>
              <th className="border-r-[1px] border-borderColor">
                <div className="font-OpenSansRegular font-[700] text-[13px] flex items-center pl-4 w-[180px] h-[40px] text-titleColor">
                  Frequency
                </div>
              </th>
              <th className="border-r-[1px] border-borderColor">
                <div className="font-OpenSansRegular font-[700] text-[13px] flex items-center pl-4 w-[180px] h-[40px] text-titleColor">
                  Created by
                </div>
              </th>
              <th className="border-r-[1px] border-borderColor">
                <div className="font-OpenSansRegular font-[700] text-[13px] flex items-center pl-4 w-[180px] h-[40px] text-titleColor">
                  Customer
                </div>
              </th>
              <th className="border-r-[1px] border-borderColor">
                <div className="font-OpenSansRegular font-[700] text-[13px] flex items-center justify-center w-[140px] h-[40px] text-titleColor">
                  Date Created
                </div>
              </th>
              <th>
                <div className="font-OpenSansRegular font-[700] text-[13px] flex items-center pl-4 w-[20px] h-[40px] text-titleColor"></div>
              </th>
            </tr>
          </thead>
          <tbody>
            {templates.map((template) => (
              <tr key={template.id} className="border-b-[1px] border-borderColor h-[40px]">
                <td className="border-r-[1px] border-borderColor">
                  <div className="font-OpenSansRegular px-[10px] py-[5px] text-[11px] font-[400] word-break-break-word">
                    {template.name}
                  </div>
                </td>
                <td className="border-r-[1px] border-borderColor">
                  <div className="font-OpenSansRegular px-[10px] py-[5px] text-[11px] font-[400] word-break-break-word">
                    {template.description}
                  </div>
                </td>
                <td className="border-r-[1px] border-borderColor">
                  <div className="font-OpenSansRegular px-[10px] py-[5px] text-[11px] font-[400]">
                    {getFormTypeLabel(template.formType)}
                  </div>
                </td>
                <td className="border-r-[1px] border-borderColor">
                  <div className="font-OpenSansRegular px-[10px] py-[5px] text-[11px] font-[400]">
                    {template.frequency !== null && template.frequency !== undefined
                      ? getFrequencyLabel(template.frequency)
                      : "-"}
                  </div>
                </td>
                <td className="border-r-[1px] border-borderColor">
                  <div className="font-OpenSansRegular px-[10px] py-[5px] text-[11px] font-[400] flex items-center gap-2">
                    {template.createdBy?.avatar && (
                      <img
                        src={getAvatarUrl(template.createdBy.avatar)}
                        className="w-[20px] h-[20px] rounded-[10px] object-contain"
                      />
                    )}
                    <span>{template.createdBy?.name}</span>
                  </div>
                </td>
                <td className="border-r-[1px] border-borderColor">
                  <div className="font-OpenSansRegular px-[10px] py-[5px] text-[11px] font-[400] word-break-break-word">
                    {template.customer?.companyName || "-"}
                  </div>
                </td>
                <td className="border-r-[1px] border-borderColor">
                  <div className="font-OpenSansRegular px-[10px] py-[5px] text-[11px] font-[400] text-center">
                    {moment(template.createdAt).format("MM/DD/YYYY")}
                  </div>
                </td>
                <td className="relative">
                  <img
                    onClick={() => setActionMenuId(actionMenuId === template.id ? null : template.id)}
                    className="cursor-pointer"
                    src={getImageUrl("/images/common/options.png")}
                    alt="Options"
                  />
                  {actionMenuId === template.id && (
                    <OutsideClickHandler onOutsideClick={() => setActionMenuId(null)}>
                      <div className="flex flex-col items-center py-1 gap-2 absolute top-[24px] right-0 w-[30px] border-[1px] rounded-[6px] border-borderColor bg-white z-20 shadow-md">
                        {tab === "Active" && (
                          <>
                            <img
                              onClick={() => handlePlusAction(template)}
                              className="w-[15px] h-[15px] object-contain cursor-pointer"
                              src={getImageUrl("/images/common/white_plus.png")}
                              alt="Create Form"
                              title="Create Form"
                            />
                            <img
                              onClick={() => handleEdit(template)}
                              className="w-[12px] h-[12px] object-contain cursor-pointer"
                              src={getImageUrl("/images/common/edit.png")}
                              alt="Edit"
                            />
                            <img
                              onClick={() => handleDuplicate(template)}
                              className="w-[15px] h-[15px] object-contain cursor-pointer"
                              src={getImageUrl("/images/common/white_duplicate.png")}
                              alt="Duplicate"
                              title="Duplicate"
                            />
                          </>
                        )}
                        <img
                          onClick={() => requestArchiveRestore(template.id)}
                          className="w-[14px] h-[14px] object-contain cursor-pointer"
                          src={getImageUrl(
                            tab === "Active"
                              ? "/images/common/white_archive.png"
                              : "/images/common/white_repeat.png"
                          )}
                          alt={tab === "Active" ? "Archive" : "Restore"}
                          title={tab === "Active" ? "Archive" : "Restore"}
                        />
                      </div>
                    </OutsideClickHandler>
                  )}
                </td>
              </tr>
            ))}
            {templates.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center py-10 text-[12px] font-OpenSansRegular text-subTitleColor">
                  No templates found
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {hideControls && (
          <div className="w-full flex items-center justify-center py-4">
            <CreateButton
              onClicked={openAddTemplateModal}
              title="ADD TEMPLATE"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplateList;
